// src/components/Navbar.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { clearHierarchyCache, useDataLayer, useCachedHierarchy, getOUForMachine, findOU } from '@quantiix/data-layer';
import { useAuth } from '@/contexts/AuthContext';
import {
    getFirstPublicView,
    getFirstMachine,
    getPublicViewByOu,
    getMachineById
} from '@/utils/hierarchyHelpers';
import { MICRO_FRONTENDS } from '@/config/MicroFrontend';
import { ROUTES, configPathFor, appPathFor, parseCurrentRoute } from '@/config/routes';
import { useToast } from '@/contexts/ToastContext';
import NavbarItem from './NavbarItem';
import { createLogger } from '@/utils/logger';
import styles from './Navbar.module.css';

import CompanyLogoRaw from '@/assets/logos/Q-full-logo.svg?raw';

import { useTranslation } from '../i18n';

const logger = createLogger('Shell::Navbar');

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const {
        companyId: activeCompanyId,
        factoryId,
        ouId,
        setScope,
        navigationSource,
        lastPvOuId,
        lastMachineId,
        configReturn
    } = useDataLayer();
    const { logout, user, hasFeature, hasAnyPermission, primaryCompany, permissions } = useAuth();
    const { addToast } = useToast();
    const navbarRef = useRef(null);

    const [isOpen, setIsOpen] = useState(false);
    const [hoverItem, setHoverItem] = useState(null);
    const [isHovered, setHovered] = useState(false);
    const [hoverTrigger, setHoverTrigger] = useState(false);
    const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
    // Ongoing-alert count for the bell badge. The shell does not fetch it — the
    // notification-center remote owns the data and publishes it on every change.
    const [alertCount, setAlertCount] = useState(0);

    const { t } = useTranslation('shell');

    // Determine which company to use
    const targetCompanyId = activeCompanyId || primaryCompany?.id;

    // Read hierarchy from cache (populated by Dashboard MFE)
    const hierarchy = useCachedHierarchy(targetCompanyId);

    const userInitials = (user?.firstName && user?.lastName)
        ? user.firstName[0] + user.lastName[0]
        : 'US';

    const account = t('navbar.account');
    const configModeLabel = t('navbar.configMode');
    const notificationsLabel = t('navbar.apps.notificationCenter');

    const currentAppId = location.pathname.startsWith('/app/')
        ? location.pathname.split('/app/')[1].split('/')[0]
        : '';

    // The bell may only show where the hub can actually mount. The hub is a
    // persistent overlay, and PersistentMFELoader mounts it only when the active
    // app id is in its `visibleWith`; anywhere else (the home page, for instance)
    // there is no listener and the click would be silently dropped.
    //
    // Declared HERE, above every effect that reads it — as a `const` it is in the
    // temporal dead zone until this line, and an effect placed above it throws
    // "Cannot access 'showBell' before initialization" at render.
    const hubConfig = MICRO_FRONTENDS['notification-hub'];
    const showBell = hasFeature(hubConfig.requiredPermission)
        && hubConfig.visibleWith.includes(currentAppId);

    const isConfigActive = currentAppId === 'config';

    const hasConfigAccess = () => hasFeature('edit_mode');

    // Determine if an MFE should show as selected in navbar
    const isAppSelected = useCallback((appKey) => {
        // PublicView/Machine: only selected if navigated via navbar
        if (appKey === 'publicview' || appKey === 'machine') {
            return currentAppId === appKey && navigationSource === 'navbar';
        }

        // Dashboard: also selected when on publicview/machine via drill-down
        if (appKey === 'dashboard') {
            if (currentAppId === 'dashboard') {
                return true;
            }
            // Drill-down to publicview/machine keeps dashboard selected
            if ((currentAppId === 'publicview' || currentAppId === 'machine') && navigationSource !== 'navbar') {
                return true;
            }
            return false;
        }

        // Other MFEs: selected if on their route
        return currentAppId === appKey;
    }, [currentAppId, navigationSource]);

    /**
     * Navigate to PublicView MFE
     */
    const navigateToPublicView = useCallback(() => {
        if (!targetCompanyId) {
            addToast({
                type: 'info',
                message: t('navbar.selectCompanyFirst'),
                duration: 3000
            });
            navigate('/app/dashboard');
            return;
        }

        if (!hierarchy) {
            // Hierarchy not cached - Dashboard hasn't loaded yet
            logger.warn('Hierarchy not cached, redirecting to dashboard');
            navigate('/app/dashboard');
            return;
        }

        // Return the user to the PublicView they last had open; the resolver
        // yields null when it's gone, inactive, or in another company.
        const targetPV = getPublicViewByOu(hierarchy, lastPvOuId)
            || getFirstPublicView(hierarchy);

        if (!targetPV) {
            addToast({
                type: 'warning',
                message: t('navbar.noPublicViewAvailable'),
                duration: 3000
            });
            return;
        }

        logger.info('Navigating to PublicView', {
            ouId: targetPV.ouId,
            ouName: targetPV.ouName,
            remembered: targetPV.ouId === lastPvOuId
        });

        setScope({
            companyId: targetPV.companyId,
            factoryId: targetPV.factoryId,
            ouId: targetPV.ouId,
            navigationSource: 'navbar'
        });

        navigate(`/app/publicview/${targetPV.ouId}`);
    }, [targetCompanyId, hierarchy, lastPvOuId, setScope, navigate, addToast, t]);

    /**
     * Navigate to Machine MFE
     */
    const navigateToMachine = useCallback(() => {
        if (!targetCompanyId) {
            addToast({
                type: 'info',
                message: t('navbar.selectCompanyFirst'),
                duration: 3000
            });
            navigate('/app/dashboard');
            return;
        }

        if (!hierarchy) {
            logger.warn('Hierarchy not cached, redirecting to dashboard');
            navigate('/app/dashboard');
            return;
        }

        // Return the user to the Machine they last had open; the resolver
        // yields null when it's gone, inactive, or in another company.
        const targetMachine = getMachineById(hierarchy, lastMachineId)
            || getFirstMachine(hierarchy);

        if (!targetMachine) {
            addToast({
                type: 'warning',
                message: t('navbar.noMachineAvailable'),
                duration: 3000
            });
            return;
        }

        logger.info('Navigating to Machine', {
            machineId: targetMachine.machine.id,
            machineName: targetMachine.machine.name,
            remembered: targetMachine.machine.id === lastMachineId
        });

        setScope({
            companyId: targetMachine.companyId,
            factoryId: targetMachine.factoryId,
            ouId: targetMachine.ouId,
            machineId: targetMachine.machine.id,
            navigationSource: 'navbar'
        });

        navigate(`/app/machine/${targetMachine.machine.id}`);
    }, [targetCompanyId, hierarchy, lastMachineId, setScope, navigate, addToast, t]);

    /**
     * Navigate to an MFE from NavBar
     */
    const navigateToApp = useCallback((appKey, path) => {
        // Check if user is an operator (operators shouldn't use this flow)
        const isOperator = permissions?.roles?.includes('operator');

        // Clear hierarchy cache for fresh start on dashboard
        if (appKey === 'dashboard' || appKey === null) {
            clearHierarchyCache(queryClient);
            setScope({ navigationSource: null });
            window.dispatchEvent(new CustomEvent('breadcrumb-update', { detail: null }));

            // Single-company non-operator user goes directly to their company
            if (appKey === 'dashboard' && primaryCompany?.id && permissions?.company?.length === 1 && !isOperator) {
                logger.info('Navigating single-company user to their company', {
                    companyId: primaryCompany.id,
                    companyName: primaryCompany.name
                });
                navigate(`/app/dashboard/company/${primaryCompany.id}`);
                return;
            }
        }

        // PublicView MFE
        if (appKey === 'publicview') {
            window.dispatchEvent(new CustomEvent('breadcrumb-update', { detail: null }));
            navigateToPublicView();
            return;
        }

        // Machine MFE
        if (appKey === 'machine') {
            window.dispatchEvent(new CustomEvent('breadcrumb-update', { detail: null }));
            navigateToMachine();
            return;
        }

        // Other MFEs
        setScope({ navigationSource: null });
        navigate(path);
    }, [queryClient, setScope, navigate, navigateToPublicView, navigateToMachine, primaryCompany, permissions]);

    /**
     * Config is a mode, not a destination: entering mirrors where the user is
     * into Config, leaving mirrors Config back into the app.
     *
     * Leaving prefers the exact route we came from, but only while the user
     * hasn't moved inside Config — that's what preserves a machine (which has no
     * Config counterpart and enters as its OU). Once they navigate in Config,
     * the mirror of where they now are is the honest answer.
     */
    // The machine id lives in the path being mirrored, not in the session — the
    // return trip mirrors the recorded origin, which may be a machine the user
    // has long since navigated away from.
    const configPathOf = useCallback((path) => {
        const { entityType, entityId } = parseCurrentRoute(path);

        return configPathFor(path, {
            machineOuId: entityType === 'machine' ? getOUForMachine(hierarchy, entityId)?.id : undefined,
            scope: { companyId: activeCompanyId, factoryId, ouId }
        });
    }, [hierarchy, activeCompanyId, factoryId, ouId]);

    // Config is where PublicViews get created, so an OU can exist there without
    // one yet. Mirroring such an OU straight back would land the user on an empty
    // PublicView, so fall back to its factory — the nearest page that has content.
    const appPathOf = useCallback((path) => {
        const mirrored = appPathFor(path);

        if (!mirrored) return null;

        const { entityType, entityId } = parseCurrentRoute(mirrored);

        if (entityType === 'publicview' && !getPublicViewByOu(hierarchy, entityId)) {
            const factory = findOU(hierarchy, entityId)?.factory;
            return factory ? ROUTES.ous(factory.id) : ROUTES.companies();
        }

        return mirrored;
    }, [hierarchy]);

    const handleConfigToggle = useCallback(() => {
        if (isConfigActive) {
            const enteredFrom = configReturn?.path;
            const unmoved = !!enteredFrom && configPathOf(enteredFrom) === location.pathname;

            navigate(unmoved ? enteredFrom : (appPathOf(location.pathname) || '/'));
            // Returning to the exact origin restores how the user got there, so
            // the navbar highlight and breadcrumbs land back as they left them.
            setScope({ navigationSource: unmoved ? (configReturn.navigationSource ?? null) : null });
            return;
        }

        setScope({
            configReturn: { path: location.pathname, navigationSource: navigationSource ?? null }
        });
        navigateToApp('config', configPathOf(location.pathname));
    }, [isConfigActive, configReturn, configPathOf, appPathOf, location.pathname, navigationSource,
        navigate, setScope, navigateToApp]);

    const handleMouseEnter = () => {
        setHovered(true);
        setTimeout(() => setHoverTrigger(!hoverTrigger), 300);
    };

    const handleMouseLeave = () => {
        setHovered(false);
        setIsOpen(false);
    };

    useEffect(() => {
        if (isHovered) {
            setIsOpen(true);
        }
    }, [hoverTrigger, isHovered]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // ── Notification bell ────────────────────────────────────────────────────
    // The navbar cannot import from a remote, so the bell and the notification
    // hub talk over CustomEvents in both directions:
    //   out  'open-notification-hub'      → the hub overlay opens
    //   in   'notification-unread-count'  → { count } badges the bell
    // The hub is mounted as a persistent overlay MFE, so it is already listening
    // on any view where the bell is visible.
    useEffect(() => {
        const onCount = (event) => setAlertCount(event.detail?.count ?? 0);
        window.addEventListener('notification-unread-count', onCount);
        return () => window.removeEventListener('notification-unread-count', onCount);
    }, []);

    // Only a mounted hub publishes the count, so a route where it does not mount
    // would otherwise keep displaying the last number it saw. Clear it there; the
    // hub re-publishes on its next mount.
    useEffect(() => {
        if (!showBell) setAlertCount(0);
    }, [showBell]);

    const handleBellClick = useCallback(() => {
        logger.info('Opening notification hub');
        // Latch, then announce. The hub is fetched lazily, so a click in the first
        // moments on a view can land before its listener exists — the latch lets a
        // hub that mounts afterwards pick the request up instead of dropping it.
        window.__QUANTIIX_NOTIFICATION_HUB_REQUESTED__ = true;
        window.dispatchEvent(new CustomEvent('open-notification-hub'));
    }, []);

    const getNavbarClasses = () => {
        const classes = [styles.navbarContainer];
        if (isOpen) classes.push(styles.isOpen);
        if (isConfigActive) classes.push(styles.isConfigMode);
        return classes.join(' ');
    };

    const getUserCircleClasses = () => {
        const classes = [styles.userSettingsCircle];
        if (account === hoverItem) classes.push(styles.isHovered);
        if (isConfigActive) classes.push(styles.isConfigMode);
        return classes.join(' ');
    };

    const getDropdownClasses = () => {
        const classes = [styles.accountDropdown];
        if (isAccountDropdownOpen) classes.push(styles.isOpen);
        return classes.join(' ');
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (navbarRef.current && !navbarRef.current.contains(event.target)) {
                setIsAccountDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Visibility check supports two gating models:
    //   - requiredPermissions (array): role-based any-of (via hasAnyPermission)
    //   - requiredPermission  (string, legacy): feature-flag check (via hasFeature)
    const isMfeVisible = (app) => {
        if (Array.isArray(app.requiredPermissions) && app.requiredPermissions.length > 0) {
            return hasAnyPermission(app.requiredPermissions);
        }
        return hasFeature(app.requiredPermission);
    };

    const accessibleMFEs = Object.entries(MICRO_FRONTENDS)
        .filter(([key, app]) => key !== 'config' && app.showInNav && isMfeVisible(app))
        .sort(([, a], [, b]) => a.order - b.order);

    return (
        <nav
            ref={navbarRef}
            className={getNavbarClasses()}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            role="navigation"
            aria-label={t('navbar.mainNavigation')}
        >
            <div
                className={`${styles.navbarLogo} ${isConfigActive ? styles.isConfigMode : ''}`}
                dangerouslySetInnerHTML={{ __html: CompanyLogoRaw }}
                onClick={() => navigateToApp(null, '/')}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') navigateToApp(null, '/');
                }}
                tabIndex={0}
                role="button"
                aria-label="Go to home"
            />

            <div className={styles.appsIconsContainer}>
                {showBell && (
                    <NavbarItem
                        isOpen={isOpen}
                        isConfigMode={isConfigActive}
                        dataId="navbar_notification_bell"
                        iconRest={MICRO_FRONTENDS['notification-center'].iconRest}
                        iconHover={MICRO_FRONTENDS['notification-center'].iconHover}
                        iconActive={MICRO_FRONTENDS['notification-center'].iconActive}
                        appName={notificationsLabel}
                        onHover={setHoverItem}
                        isHovered={notificationsLabel === hoverItem}
                        isSelected={currentAppId === 'notification-center'}
                        notifications={alertCount}
                        onClick={handleBellClick}
                    />
                )}

                {accessibleMFEs.map(([key, app]) => {
                    const label = app.labelKey ? t(app.labelKey) : app.name;
                    return (
                        <NavbarItem
                            key={key}
                            isOpen={isOpen}
                            isConfigMode={isConfigActive}
                            dataId={`navbar_${key}`}
                            iconRest={app.iconRest}
                            iconHover={app.iconHover}
                            iconActive={app.iconActive}
                            appName={label}
                            onHover={setHoverItem}
                            isHovered={label === hoverItem}
                            isSelected={isAppSelected(key)}
                            onClick={() => navigateToApp(key, `/app/${key}`)}
                        />
                    );
                })}
            </div>

            <div className={styles.bottomIconsContainer}>
                {hasConfigAccess() && (
                    <NavbarItem
                        isOpen={isOpen}
                        isConfigMode={isConfigActive}
                        dataId="navbar_config_mode"
                        iconRest={MICRO_FRONTENDS.config.iconRest}
                        iconHover={MICRO_FRONTENDS.config.iconHover}
                        iconActive={MICRO_FRONTENDS.config.iconActive}
                        appName={configModeLabel}
                        onHover={setHoverItem}
                        isHovered={configModeLabel === hoverItem}
                        isSelected={isConfigActive}
                        onClick={handleConfigToggle}
                    />
                )}

                <div className={styles.accountSection}>
                    <NavbarItem
                        isOpen={isOpen}
                        isConfigMode={isConfigActive}
                        dataId="navbar_settings"
                        appName={account}
                        onHover={setHoverItem}
                        isHovered={account === hoverItem}
                        onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
                    >
                        <div className={styles.iconContainer}>
                            <div className={getUserCircleClasses()} aria-label="User settings">
                                {userInitials}
                            </div>
                        </div>
                    </NavbarItem>

                    {isAccountDropdownOpen && (
                        <div className={getDropdownClasses()} role="menu">
                            <button
                                className={styles.dropdownItem}
                                onClick={() => {
                                    setIsAccountDropdownOpen(false);
                                    navigate('/app/settings');
                                }}
                                role="menuitem"
                            >
                                {t('navbar.menu.settings')}
                            </button>

                            {hasFeature('user_management') && (
                                <button
                                    className={styles.dropdownItem}
                                    onClick={() => {
                                        setIsAccountDropdownOpen(false);
                                        window.dispatchEvent(new CustomEvent('open-user-management'));
                                    }}
                                    role="menuitem"
                                >
                                    {t('navbar.menu.adminSettings')}
                                </button>
                            )}

                            <div className={styles.dropdownDivider} />

                            <button
                                className={styles.dropdownItem}
                                onClick={handleLogout}
                                role="menuitem"
                            >
                                {t('navbar.menu.logOut')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
};

export default Navbar;